import { z } from "zod/v3";

import { enumIncludes } from "~/lib/parse-enum-value";

export const MODIFIER_TYPES = ["modifier", "sub_modifier"] as const;
export const isModifierType = enumIncludes(MODIFIER_TYPES);

export const modifierSchema = z.object({
  modifierGroupId: z.string().cuid2(),
  name: z
    .string()
    .min(1, "Please enter a name")
    .max(40, "The name is too long"),
  additionalCost: z.string().regex(/^\d{1,3}\.\d{2}$/, {
    message: "Invalid modifier price",
  }),
  isOutOfStock: z.boolean(),
  sortOrder: z.number().int().nonnegative(),
  canBeMultiplied: z.boolean(),
});

export const modifierWithIdSchema = modifierSchema
  .omit({
    sortOrder: true,
  })
  .extend({
    id: z.string(),
  });

export type ModifierSchema = z.infer<typeof modifierSchema>;
export type ModifierWithIdSchema = z.infer<typeof modifierWithIdSchema>;

const modifierGroupBaseSchema = z.object({
  id: z.string().cuid2(),
  name: z
    .string()
    .min(1, "Please enter a name")
    .max(40, "The name is too long"),
  minimum: z.number().int().nonnegative(),
  maximum: z.number().int().nonnegative(),
  type: z.enum(["modifier", "sub_modifier"]),
  parentModifierGroupId: z.string().cuid2().nullable(),
  sortOrder: z.number().int().nonnegative(),
  organizationId: z.string(),
  modifiers: z.array(modifierSchema),
});

export const modifierGroupSchema = modifierGroupBaseSchema.extend({
  subModifierGroups: z.array(
    modifierGroupBaseSchema.omit({ id: true, organizationId: true })
  ),
});

export const modifierGroupWithIdsSchema = modifierGroupBaseSchema.extend({
  modifiers: z.array(modifierSchema.extend({ id: z.string() })),
  subModifierGroups: z.array(
    modifierGroupBaseSchema.extend({
      modifiers: z.array(modifierSchema.extend({ id: z.string() })),
    })
  ),
});

export type ModifierGroupWithIdSchema = z.infer<
  typeof modifierGroupWithIdsSchema
>;

export const modifierGroupValidationSchema = modifierGroupSchema.refine(
  (data) => data.maximum >= data.minimum,
  {
    message: "Maximum must be greater than or equal to minimum",
    path: ["maximum"],
  }
);

export type ModifierGroupSchema = z.infer<typeof modifierGroupSchema>;

export const modifierGroupApplyToSchema = z
  .object({
    categories: z.array(z.string().cuid2()),
    items: z.array(z.string().cuid2()),
  })
  .refine((data) => data.categories.length > 0 || data.items.length > 0, {
    message: "Please select at least one category or item",
  });

export const modifierGroupFormModifierListSchema = z.discriminatedUnion(
  "modifierType",
  [
    z.object({
      itemId: z.string().cuid2(),
      modifierType: z.literal("modifier"),
      name: z.string().min(1, "Please enter a name"),
      sortOrder: z.number().int().nonnegative(),
      accordionOpen: z.boolean().optional(),
      data: modifierSchema.omit({
        name: true,
        modifierGroupId: true,
        sortOrder: true,
      }),
    }),
    z.object({
      itemId: z.string().cuid2(),
      modifierType: z.literal("sub_modifier"),
      name: z.string().min(1, "Please enter a name"),
      sortOrder: z.number().int().nonnegative(),
      accordionOpen: z.boolean().optional(),
      data: modifierGroupSchema
        .omit({
          id: true,
          name: true,
          sortOrder: true,
          subModifierGroups: true,
          organizationId: true,
        })
        .extend({
          modifiers: z.array(
            modifierSchema
              .omit({
                modifierGroupId: true,
              })
              .extend({
                itemId: z.string().cuid2(),
              })
          ),
        }),
    }),
  ]
);
export type ModifierGroupFormModifierListSchema = z.infer<
  typeof modifierGroupFormModifierListSchema
>;

const modifierFormSchemaBase = modifierGroupSchema
  .omit({ organizationId: true })
  .extend({
    modifiersList: z.array(modifierGroupFormModifierListSchema),
  });

export const modifierFormSchema = modifierFormSchemaBase
  .refine((data) => data.maximum >= data.minimum, {
    message: "Maximum must be greater than or equal to minimum",
    path: ["maximum"],
  })
  .refine(
    (data) => {
      return data.modifiersList.every((item) => {
        if (item.modifierType === "sub_modifier") {
          return item.data.maximum >= item.data.minimum;
        }
        return true;
      });
    },
    {
      message: "Submodifier maximum must be greater than or equal to minimum",
      path: ["modifiersList"],
    }
  );

export type ModifierFormSchema = z.infer<typeof modifierFormSchemaBase>;
